/**
 * Student Documents Management Component
 * Allows agents to upload and manage documents for their students
 */
import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { agentStudentsAPI } from '@/lib/api';
import { 
  FileText, 
  Upload, 
  Trash2, 
  Loader2, 
  File,
  FileImage,
  X
} from 'lucide-react';

const DOCUMENT_TYPES = [
  { value: 'passport', label: 'Passport' },
  { value: 'cv', label: 'CV / Resume' },
  { value: 'transcript', label: 'Academic Transcript' },
  { value: 'degree_certificate', label: 'Degree Certificate' },
  { value: 'english_test', label: 'English Test Result (IELTS/TOEFL)' },
  { value: 'recommendation_letter', label: 'Recommendation Letter' },
  { value: 'personal_statement', label: 'Personal Statement' },
  { value: 'financial_document', label: 'Financial Document' },
  { value: 'other', label: 'Other Document' }
];

export const StudentDocuments = ({ student, open, onClose }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [documentType, setDocumentType] = useState('passport');
  const [selectedFile, setSelectedFile] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open && student) {
      fetchDocuments();
    }
  }, [open, student]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await agentStudentsAPI.getDocuments(student.id);
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !documentType) {
      toast.error('Please select a document type and file');
      return;
    }

    setUploading(true);
    try {
      await agentStudentsAPI.uploadDocument(student.id, documentType, selectedFile);
      toast.success('Document uploaded successfully');
      setSelectedFile(null);
      setDocumentType('passport');
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    setDeletingId(documentId);
    try {
      await agentStudentsAPI.deleteDocument(student.id, documentId);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    } finally {
      setDeletingId(null);
    }
  };

  const getDocumentTypeLabel = (type) => {
    const docType = DOCUMENT_TYPES.find(d => d.value === type);
    return docType ? docType.label : type;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (contentType) => {
    if (contentType?.includes('image')) return <FileImage className="w-5 h-5 text-blue-600" />;
    if (contentType?.includes('pdf')) return <FileText className="w-5 h-5 text-red-600" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#f1f3f5] border-gray-200 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-gray-800 flex items-center gap-2">
            <FileText className="w-5 h-5 text-teal-600" />
            Documents for {student?.full_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Upload Section */}
          <div className="p-4 bg-[#e9ecef]/50 rounded-xl border border-dashed border-gray-300">
            <h4 className="text-gray-800 font-medium mb-3">Upload New Document</h4>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Select value={documentType} onValueChange={setDocumentType}>
                  <SelectTrigger className="bg-[#e9ecef] border-gray-200">
                    <SelectValue placeholder="Document Type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#e9ecef] border-gray-200">
                    {DOCUMENT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  className="hidden"
                  id="document-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="border-gray-300 text-gray-600 hover:bg-gray-100"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Choose File
                </Button>
              </div>

              {selectedFile && (
                <div className="flex items-center justify-between p-3 bg-gray-200/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    {getFileIcon(selectedFile.type)}
                    <span className="text-gray-600 text-sm truncate max-w-[200px]">{selectedFile.name}</span>
                    <span className="text-gray-400 text-xs">({formatFileSize(selectedFile.size)})</span>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    className="text-gray-500 hover:text-red-600"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}

              <Button
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                className="w-full bg-teal-600 text-white hover:bg-teal-700 font-semibold"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Document
                  </>
                )}
              </Button>
            </div>
            <p className="text-gray-400 text-xs mt-2">Supported formats: PDF, JPG, PNG, DOC, DOCX. Max size: 10MB</p>
          </div>

          {/* Documents List */}
          <div>
            <h4 className="text-gray-800 font-medium mb-3">Uploaded Documents ({documents.length})</h4>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No documents uploaded yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-[#e9ecef]/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {getFileIcon(doc.content_type)}
                      <div>
                        <p className="text-gray-800 text-sm font-medium">{doc.filename}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className="bg-blue-100 text-blue-600 text-xs">
                            {getDocumentTypeLabel(doc.type)}
                          </Badge>
                          <span className="text-gray-400 text-xs">{formatFileSize(doc.size)}</span>
                          <span className="text-gray-400 text-xs">
                            {new Date(doc.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(doc.id)}
                      disabled={deletingId === doc.id}
                      className="text-red-600 hover:bg-red-500/10"
                    >
                      {deletingId === doc.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StudentDocuments;
